import {
  AfterContentInit,
  Component,
  ContentChildren,
  Directive,
  Input,
  OnChanges,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { Observable, Subject, filter, first, isObservable, map, of, startWith, takeUntil } from 'rxjs';

@Directive({
  selector: '[wizardStep]',
})
export class WizardStepDirective implements OnChanges {
  @Input('wizardStep') name: string;
  @Input() isComplete: boolean = true;
  @Input() forwardAction: () => boolean | Observable<boolean>;
  @Input() rewindAction: () => boolean | Observable<boolean>;

  readonly changes: Observable<void>;

  private changes$ = new Subject<void>();

  constructor(public templateRef: TemplateRef<unknown>) {
    this.changes = this.changes$.asObservable();
  }

  ngOnChanges(): void {
    this.changes$.next();
  }
}

@Component({
  selector: 'insol-wizard',
  templateUrl: './wizard.component.html',
})
export class WizardComponent implements AfterContentInit {
  @ContentChildren(WizardStepDirective) wizardSteps: QueryList<WizardStepDirective>;

  activeStepContent: TemplateRef<unknown>;
  activeStepName: string;

  readonly changes: Observable<void>;

  private changes$ = new Subject<void>();
  private steps: WizardStepDirective[];

  constructor() {
    this.changes = this.changes$.asObservable();
  }

  get canBack(): boolean {
    return this.getActiveStepIndex() > 0;
  }

  get canNext(): boolean {
    const activeStepIndex = this.getActiveStepIndex();
    const lastStepIndex = this.getLastStepIndex();
    const activeStep = this.getActiveStep();
    return activeStepIndex >= 0 && activeStepIndex < lastStepIndex && !!activeStep?.isComplete;
  }

  get canFinish(): boolean {
    const activeStepIndex = this.getActiveStepIndex();
    const lastStepIndex = this.getLastStepIndex();
    const activeStep = this.getActiveStep();
    return activeStepIndex === lastStepIndex && !!activeStep?.isComplete;
  }

  ngAfterContentInit(): void {
    this.wizardSteps.changes
      .pipe(
        map(() => this.wizardSteps.toArray()),
        startWith(this.wizardSteps.toArray())
      )
      .subscribe((steps) => {
        if (!steps.length) {
          throw new Error('Wizard must have at least one step.');
        }

        if (steps.some((step) => !step.name) || new Set(steps.map((step) => step.name)).size !== steps.length) {
          throw new Error('Each wizard step must have a unique name');
        }

        this.steps = steps;

        // Activate first step if there is no active step or the current step is no longer valid
        if (!this.getActiveStep()) {
          this.reset();
        } else {
          // Notify of changes
          this.changes$.next();
        }
      });
  }

  nextStep(): void {
    if (this.canNext) {
      this.invokeAction(this.getActiveStep().forwardAction)
        .pipe(first())
        .subscribe((success) => {
          if (success) {
            const nextIndex = this.getActiveStepIndex() + 1;
            this.activateStep(this.steps[nextIndex]);
          }
        });
    }
  }

  previousStep(): void {
    if (this.canBack) {
      this.invokeAction(this.getActiveStep().rewindAction)
        .pipe(first())
        .subscribe((success) => {
          if (success) {
            const previousIndex = this.getActiveStepIndex() - 1;
            this.activateStep(this.steps[previousIndex]);
          }
        });
    }
  }

  goToStep(step: WizardStepDirective): void {
    if (this.canGoToStep(step)) {
      const activeStep = this.getActiveStep();
      const stepIndex = this.getStepIndex(step);
      const currentIndex = this.getStepIndex(activeStep);

      this.invokeAction(stepIndex < currentIndex ? activeStep.rewindAction : activeStep.forwardAction)
        .pipe(first())
        .subscribe((success) => {
          if (success) {
            this.activateStep(step);
          }
        });
    }
  }

  canGoToStep(step: WizardStepDirective): boolean {
    const stepIndex = this.getStepIndex(step);
    const previousSteps = this.steps.slice(0, stepIndex);
    return previousSteps.every((s) => s.isComplete);
  }

  reset(): void {
    const firstStep = this.steps[0];
    if (firstStep) {
      // activateStep also notifies of changes
      this.activateStep(firstStep);
    } else {
      this.activeStepName = null;
      this.activeStepContent = null;
      this.changes$.next();
    }
  }

  private activateStep(step: WizardStepDirective): void {
    this.activeStepName = step.name;
    this.activeStepContent = step.templateRef;
    this.changes$.next();

    // Notify if anything about the active step changes, such as isComplete
    this.getActiveStep()
      .changes.pipe(takeUntil(this.changes.pipe(filter(() => this.activeStepName !== step.name))))
      .subscribe(() => this.changes$.next());
  }

  private getActiveStepIndex(): number {
    const index = this.steps?.findIndex((step) => step.name === this.activeStepName);
    return index != null ? index : -1;
  }

  private getLastStepIndex(): number {
    return (this.steps?.length || 0) - 1;
  }

  private getStepIndex(step: WizardStepDirective): number {
    return this.steps.findIndex((s) => s.name === step.name);
  }

  private getActiveStep(): WizardStepDirective {
    return this.steps?.find((step) => step.name === this.activeStepName);
  }

  private invokeAction(action: () => boolean | Observable<boolean>): Observable<boolean> {
    action = action || (() => true);
    const result = action();
    if (isObservable(result)) {
      return result;
    }
    return of(result);
  }
}
